/**
 * Accessibility Module for WCAG 2.1 AA Support
 * 
 * This module provides keyboard usability and accessibility enhancements
 * for Shopify Dawn theme without modifying base Dawn behavior.
 * 
 * @module accessibility
 */

(function() {
  'use strict';

  /**
   * Internal flag to prevent duplicate initialization
   */
  let _isInitialized = false;

  /**
   * Storage for event listeners to prevent duplicate attachment
   */
  const _listeners = {
    escapeHandler: null,
    skipLinkHandler: null
  };

  /**
   * Setup accessibility enhancements for WCAG 2.1 AA compliance
   * 
   * Features:
   * 1. Skip link enhancement - Ensures proper focus and scroll behavior
   * 2. Escape key handling - Adds global escape key support for modals/drawers
   * 3. Aria-live region - Conditional creation for toast notifications
   * 
   * @param {Object} ctx - Configuration context
   * @param {boolean} [ctx.enableAriaLive=false] - Whether to create aria-live region
   * @param {boolean} [ctx.force=false] - Force re-initialization
   * 
   * @example
   * // Basic usage
   * setupAccessibility({ enableAriaLive: false });
   * 
   * // With aria-live for toast module
   * setupAccessibility({ enableAriaLive: true });
   */
  function setupAccessibility(ctx = {}) {
  // Prevent duplicate initialization unless forced
  if (_isInitialized && !ctx.force) {
    if (window.cyberSellsConfig?.debug) {
      console.debug('[Accessibility] Already initialized. Use ctx.force=true to re-initialize.');
    }
    return;
  }

  if (window.cyberSellsConfig?.debug) {
    console.debug('[Accessibility] Initializing accessibility module...');
  }

  // Enhancement 1: Skip Link Functionality
  enhanceSkipLink();

  // Enhancement 2: Global Escape Key Handler
  enhanceEscapeKeyHandling();

  // Enhancement 3: Aria-live Region (conditional)
  if (ctx.enableAriaLive) {
    ensureAriaLiveRegion();
  }

  _isInitialized = true;
  
  if (window.cyberSellsConfig?.debug) {
    console.debug('[Accessibility] Initialization complete');
  }
}

  /**
   * Enhances skip-to-content link if it exists
   * 
   * Improvements:
   * - Ensures focus moves to main content
   * - Prevents scroll position weirdness
   * - Maintains keyboard navigation state
   * 
   * Keyboard Test: Tab on page load, press Enter on skip link
   * Expected: Focus moves to main content without page jump
   */
  function enhanceSkipLink() {
  // Safe no-op if DOM not ready
  if (!document.querySelector) return;
  
  const skipLink = document.querySelector('a.skip-to-content-link[href="#MainContent"]');
  
  if (!skipLink) {
    if (window.cyberSellsConfig?.debug) {
      console.debug('[Accessibility] No skip link found - skipping enhancement');
    }
    return;
  }

  // Remove existing listener if present (idempotent)
  if (_listeners.skipLinkHandler) {
    skipLink.removeEventListener('click', _listeners.skipLinkHandler);
  }

  _listeners.skipLinkHandler = (event) => {
    event.preventDefault();
    
    const mainContent = document.getElementById('MainContent');
    if (!mainContent) {
      if (window.cyberSellsConfig?.debug) {
        console.warn('[Accessibility] MainContent target not found');
      }
      return;
    }

    // Ensure the target is focusable
    if (!mainContent.hasAttribute('tabindex')) {
      mainContent.setAttribute('tabindex', '-1');
    }

    // Focus the main content
    mainContent.focus();

    // Scroll to the element smoothly without jarring jumps
    // Using preventScroll and manual smooth scroll for better UX
    const targetPosition = mainContent.getBoundingClientRect().top + window.pageYOffset;
    const headerOffset = 100; // Account for sticky headers
    
    window.scrollTo({
      top: targetPosition - headerOffset,
      behavior: 'smooth'
    });
  };

  skipLink.addEventListener('click', _listeners.skipLinkHandler);
  
  if (window.cyberSellsConfig?.debug) {
    console.debug('[Accessibility] Skip link enhanced');
  }
}

  /**
   * Adds global Escape key handling for modals and drawers
   * 
   * This is a SAFE implementation that:
   * - Only acts on elements that don't have their own escape handlers
   * - Uses event delegation to avoid conflicts
   * - Respects Dawn's existing modal/drawer escape key behavior
   * 
   * Keyboard Test: Open cart drawer or modal, press Escape
   * Expected: Modal/drawer closes and focus returns to trigger element
   */
  function enhanceEscapeKeyHandling() {
  // Check if Dawn components already handle escape
  // CartDrawer and DetailsModal already have escape handlers
  // We only add a global handler as a fallback for any missed cases
  
  // Remove existing listener if present (idempotent)
  if (_listeners.escapeHandler) {
    document.removeEventListener('keydown', _listeners.escapeHandler);
  }

  _listeners.escapeHandler = (event) => {
    if (event.key !== 'Escape' && event.code !== 'Escape') {
      return;
    }

    // Check if we're inside a component that already handles escape
    // Dawn components: cart-drawer, details-modal, modal-dialog, etc.
    const dawnComponents = [
      'cart-drawer',
      'details-modal', 
      'modal-dialog',
      'product-modal'
    ];

    for (const componentName of dawnComponents) {
      const component = event.target.closest(componentName);
      if (component) {
        // Dawn component will handle it - we don't interfere
        if (window.cyberSellsConfig?.debug) {
          console.debug(`[Accessibility] Escape handled by ${componentName}`);
        }
        return;
      }
    }

    // Check for any open details elements (Dawn uses these for disclosures)
    const openDetails = event.target.closest('details[open]');
    if (openDetails) {
      // Dawn's onKeyUpEscape in global.js already handles this
      if (window.cyberSellsConfig?.debug) {
        console.debug('[Accessibility] Escape handled by details element');
      }
      return;
    }

    // If we get here, no Dawn component is handling escape
    // This is our fallback for any future modals/overlays
    if (window.cyberSellsConfig?.debug) {
      console.debug('[Accessibility] Global escape key handler - no action needed');
    }
  };

  // Use capture phase to see the event before it bubbles
  // But we don't stopPropagation to avoid breaking Dawn's handlers
  document.addEventListener('keydown', _listeners.escapeHandler, false);
  
  if (window.cyberSellsConfig?.debug) {
    console.debug('[Accessibility] Escape key handling enhanced (non-invasive)');
  }
}

  /**
   * Creates or ensures an aria-live region exists for announcements
   * 
   * ONLY called if ctx.enableAriaLive is true (toast module contract)
   * Otherwise, the toast module or integrator handles it
   * 
   * Usage: When toast module needs announcements
   * Expected: Screen reader announces messages without page navigation
   */
  function ensureAriaLiveRegion() {
  // Safe no-op if DOM not ready
  if (!document.querySelector || !document.body) return;
  
  const existingRegion = document.querySelector('[role="status"][aria-live="polite"]');
  
  if (existingRegion) {
    if (window.cyberSellsConfig?.debug) {
      console.debug('[Accessibility] Aria-live region already exists');
    }
    return;
  }

  const liveRegion = document.createElement('div');
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('aria-atomic', 'true');
  liveRegion.className = 'visually-hidden'; // Use Dawn's existing visually-hidden class
  liveRegion.id = 'accessibility-live-region';
  
  document.body.appendChild(liveRegion);
  
  if (window.cyberSellsConfig?.debug) {
    console.debug('[Accessibility] Aria-live region created');
  }
}

  /**
   * Helper function to announce a message to screen readers
   * 
   * @param {string} message - Message to announce
   * @param {number} [clearDelay=3000] - Time in ms before clearing the message
   * 
   * @example
   * announceToScreenReader('Item added to cart');
   */
  function announceToScreenReader(message, clearDelay = 3000) {
  const liveRegion = document.getElementById('accessibility-live-region');
  
  if (!liveRegion) {
    console.warn('[Accessibility] Aria-live region not found. Call setupAccessibility({ enableAriaLive: true }) first.');
    return;
  }

  liveRegion.textContent = message;
  
  // Clear after delay to allow the same message to be announced again
  setTimeout(() => {
    liveRegion.textContent = '';
  }, clearDelay);
}

  /**
   * Cleanup function to remove all event listeners
   * Useful for testing or hot module replacement
   */
  function cleanup() {
    if (_listeners.skipLinkHandler) {
      const skipLink = document.querySelector('a.skip-to-content-link[href="#MainContent"]');
      if (skipLink) {
        skipLink.removeEventListener('click', _listeners.skipLinkHandler);
      }
    }

    if (_listeners.escapeHandler) {
      document.removeEventListener('keydown', _listeners.escapeHandler);
    }

    _isInitialized = false;
    
    if (window.cyberSellsConfig?.debug) {
      console.debug('[Accessibility] Cleanup complete');
    }
  }

  // Expose module functions
  window.CyberSells_accessibility = {
    setupAccessibility,
    announceToScreenReader,
    cleanup
  };

})();
